import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { ArrowLeft, Plus, Edit, Trash2, GripVertical, Type, Hash, Calendar, List, AlignLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../services/api';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Číslo', icon: Hash },
  { value: 'textarea', label: 'Dlouhý text', icon: AlignLeft },
  { value: 'select', label: 'Výběr', icon: List },
  { value: 'date', label: 'Datum', icon: Calendar },
];

export const FormBuilderPage = () => {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [baseFields, setBaseFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [baseFieldDialogOpen, setBaseFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingBaseField, setEditingBaseField] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    field_type: 'text',
    required: false,
    options: [],
    width: 'full',
    placeholder: ''
  });
  const [baseFieldFormData, setBaseFieldFormData] = useState({
    label: '',
    required: false,
    placeholder: ''
  });
  const [optionInput, setOptionInput] = useState('');

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const [fieldsRes, baseFieldsRes] = await Promise.all([
        api.get('/form-fields'),
        api.get('/base-fields')
      ]);
      setFields(fieldsRes.data);
      setBaseFields(baseFieldsRes.data);
    } catch (error) {
      toast.error('Chyba při načítání polí');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBaseFieldDialog = (baseField) => {
    setEditingBaseField(baseField);
    setBaseFieldFormData({
      label: baseField.label,
      required: baseField.required,
      placeholder: baseField.placeholder || ''
    });
    setBaseFieldDialogOpen(true);
  };

  const handleSaveBaseField = async () => {
    if (!baseFieldFormData.label.trim()) {
      toast.error('Název pole je povinný');
      return;
    }

    try {
      await api.put(`/base-fields/${editingBaseField.id}`, baseFieldFormData);
      toast.success('Základní pole aktualizováno');
      setBaseFieldDialogOpen(false);
      fetchFields();
    } catch (error) {
      toast.error('Chyba při ukládání základního pole');
    }
  };

  const handleOpenDialog = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        label: field.label,
        field_type: field.field_type,
        required: field.required,
        options: field.options || [],
        width: field.width || 'full',
        placeholder: field.placeholder || ''
      });
    } else {
      setEditingField(null);
      setFormData({
        label: '',
        field_type: 'text',
        required: false,
        options: [],
        width: 'full',
        placeholder: ''
      });
    }
    setOptionInput('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.label.trim()) {
      toast.error('Název pole je povinný');
      return;
    }

    if (formData.field_type === 'select' && formData.options.length === 0) {
      toast.error('Přidejte alespoň jednu možnost výběru');
      return;
    }

    try {
      if (editingField) {
        await api.put(`/form-fields/${editingField.id}`, formData);
        toast.success('Pole aktualizováno');
      } else {
        await api.post('/form-fields', { ...formData, order: fields.length });
        toast.success('Pole vytvořeno');
      }
      setDialogOpen(false);
      fetchFields();
    } catch (error) {
      toast.error('Chyba při ukládání pole');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Opravdu chcete smazat toto pole?')) return;

    try {
      await api.delete(`/form-fields/${id}`);
      toast.success('Pole smazáno');
      fetchFields();
    } catch (error) {
      toast.error('Chyba při mazání pole');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    setFields(updatedItems);

    // Save to backend
    try {
      const response = await api.put('/form-fields/reorder', {
        fields: updatedItems.map(item => ({
          id: item.id,
          order: item.order,
          width: item.width || 'full'
        }))
      });
      
      if (response.status === 200) {
        toast.success('Pořadí aktualizováno');
      }
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Chyba při změně pořadí');
      fetchFields(); // Reload on error
    }
  };

  const handleAddOption = () => {
    if (!optionInput.trim()) return;
    setFormData({
      ...formData,
      options: [...formData.options, optionInput.trim()]
    });
    setOptionInput('');
  };

  const handleRemoveOption = (index) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index)
    });
  };

  const getFieldIcon = (type) => {
    const fieldType = FIELD_TYPES.find(t => t.value === type);
    return fieldType ? fieldType.icon : Type;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-500">Načítání...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8" data-testid="form-builder-page">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zpět
          </Button>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Editor formuláře</h1>
            <p className="text-lg text-slate-600">Nastavení vlastních polí pro hlášení</p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-field-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Přidat pole
          </Button>
        </div>

        {/* Base Fields Canvas */}
        <div className="bg-white rounded-xl border border-cyan-200 p-6 shadow-sm mb-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Základní pole formuláře (nelze smazat)
          </h3>
          
          <div className="space-y-2">
            {baseFields.map((field) => {
              const Icon = getFieldIcon(field.field_type);
              return (
                <div
                  key={field.id}
                  className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 flex items-center gap-3"
                  data-testid={`base-field-item-${field.id}`}
                >
                  <Icon className="h-5 w-5 text-cyan-600" />
                  
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {FIELD_TYPES.find(t => t.value === field.field_type)?.label}
                    </div>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleOpenBaseFieldDialog(field)}
                    data-testid={`edit-base-field-${field.id}`}
                    className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-100"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom Fields Canvas */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Vlastní pole ({fields.length} polí)
          </h3>
          
          {fields.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Type className="h-12 w-12 mx-auto mb-3" />
              <p>Zatím žádná vlastní pole</p>
              <p className="text-sm">Přidejte pole pro rozšíření formuláře hlášení</p>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="fields">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-2"
                  >
                    {fields.map((field, index) => {
                      const Icon = getFieldIcon(field.field_type);
                      return (
                        <Draggable key={field.id} draggableId={field.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center gap-3 ${
                                snapshot.isDragging ? 'shadow-lg' : ''
                              }`}
                              data-testid={`field-item-${field.id}`}
                            >
                              <div
                                {...provided.dragHandleProps}
                                className="text-slate-400 hover:text-slate-600 cursor-grab"
                              >
                                <GripVertical className="h-5 w-5" />
                              </div>
                              
                              <Icon className="h-5 w-5 text-slate-600" />
                              
                              <div className="flex-1">
                                <div className="font-medium text-slate-900">
                                  {field.label}
                                  {field.required && <span className="text-red-500 ml-1">*</span>}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {FIELD_TYPES.find(t => t.value === field.field_type)?.label} • 
                                  {field.width === 'full' ? ' Celá šířka' : ' Polovina šířky'}
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenDialog(field)}
                                  data-testid={`edit-field-${field.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(field.id)}
                                  data-testid={`delete-field-${field.id}`}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="field-dialog">
            <DialogHeader>
              <DialogTitle>
                {editingField ? 'Upravit pole' : 'Nové pole'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="label">Název pole *</Label>
                  <Input
                    id="label"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="např. Místo výskytu"
                    data-testid="field-label-input"
                  />
                </div>

                <div>
                  <Label htmlFor="field-type">Typ pole *</Label>
                  <Select
                    value={formData.field_type}
                    onValueChange={(value) => setFormData({ ...formData, field_type: value })}
                  >
                    <SelectTrigger id="field-type" data-testid="field-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="width">Šířka</Label>
                  <Select
                    value={formData.width}
                    onValueChange={(value) => setFormData({ ...formData, width: value })}
                  >
                    <SelectTrigger id="width" data-testid="field-width-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Celá šířka</SelectItem>
                      <SelectItem value="half">Polovina šířky</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="placeholder">Placeholder</Label>
                  <Input
                    id="placeholder"
                    value={formData.placeholder}
                    onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                    placeholder="Pomocný text..."
                    data-testid="field-placeholder-input"
                  />
                </div>

                <div className="col-span-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="required"
                      checked={formData.required}
                      onCheckedChange={(checked) => setFormData({ ...formData, required: checked })}
                      data-testid="field-required-switch"
                    />
                    <Label htmlFor="required" className="cursor-pointer">
                      Povinné pole
                    </Label>
                  </div>
                </div>

                {formData.field_type === 'select' && (
                  <div className="col-span-2">
                    <Label>Možnosti výběru *</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        value={optionInput}
                        onChange={(e) => setOptionInput(e.target.value)}
                        placeholder="Přidat možnost..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddOption();
                          }
                        }}
                        data-testid="option-input"
                      />
                      <Button
                        type="button"
                        onClick={handleAddOption}
                        data-testid="add-option-button"
                      >
                        Přidat
                      </Button>
                    </div>
                    <div className="mt-3 space-y-2">
                      {formData.options.map((option, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 bg-slate-50 p-2 rounded"
                          data-testid={`option-${index}`}
                        >
                          <span className="flex-1 text-sm">{option}</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveOption(index)}
                            data-testid={`remove-option-${index}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                data-testid="cancel-dialog-button"
              >
                Zrušit
              </Button>
              <Button
                onClick={handleSave}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="save-field-button"
              >
                Uložit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Base Field Edit Dialog */}
        <Dialog open={baseFieldDialogOpen} onOpenChange={setBaseFieldDialogOpen}>
          <DialogContent data-testid="base-field-dialog">
            <DialogHeader>
              <DialogTitle>Upravit základní pole</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="base-label">Název pole *</Label>
                <Input
                  id="base-label"
                  value={baseFieldFormData.label}
                  onChange={(e) => setBaseFieldFormData({ ...baseFieldFormData, label: e.target.value })}
                  placeholder="např. Název"
                  data-testid="base-field-label-input"
                />
              </div>

              <div>
                <Label htmlFor="base-placeholder">Placeholder</Label>
                <Input
                  id="base-placeholder"
                  value={baseFieldFormData.placeholder}
                  onChange={(e) => setBaseFieldFormData({ ...baseFieldFormData, placeholder: e.target.value })}
                  placeholder="Pomocný text..."
                  data-testid="base-field-placeholder-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="base-required"
                  checked={baseFieldFormData.required}
                  onCheckedChange={(checked) => setBaseFieldFormData({ ...baseFieldFormData, required: checked })}
                  data-testid="base-field-required-switch"
                />
                <Label htmlFor="base-required" className="cursor-pointer">
                  Povinné pole
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setBaseFieldDialogOpen(false)}
                data-testid="cancel-base-dialog-button"
              >
                Zrušit
              </Button>
              <Button
                onClick={handleSaveBaseField}
                className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600"
                data-testid="save-base-field-button"
              >
                Uložit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
